<template>
  <div class="home">
    <page-header :heading="'Start a scan'" />
    <div class="wrapper">
      <div
        class="max-w-2xl mx-auto px-8 py-8 -mt-12 bg-white rounded-lg shadow-md"
      >
        <h2 class="mb-4 text-lg text-center border-b-2 pb-3 border-gray-100">
          Scan a web address
        </h2>
        <label for="url">Web address (http or https required)</label>
        <input
          name="url"
          class="w-full"
          v-model="scan.url"
          type="text"
          placeholder="Enter a web address"
        />
        <div class="flex flex-wrap mt-4">
          <div class="md:w-4/12">
            <label for="device">Device:</label>
            <select name="device" v-model="device">
              <option v-for="device in devices" :key="device.name">
                {{ device.name }}
              </option>
            </select>
          </div>
          <div class="md:w-4/12 md:pl-2">
            <label for="type">Scan type</label>
            <select name="type" v-model="scan.type">
              <option value="overlay">heatmap</option>
              <option value="bw">greyscale</option>
              <option value="black">dark zones</option>
              <option value="white">focus fog</option>
            </select>
          </div>
          <div class="md:w-4/12 md:pl-2">
            <label for="location">Scan location</label>
            <select name="location" v-model="scan.location">
              <option value="us">United States</option>
              <option value="gb">United Kingdom</option>
              <option value="au">Australia</option>
              <option value="in">India</option>
              <option value="br">Brazil</option>
            </select>
          </div>
        </div>
        <div class="flex flex-wrap mt-4" v-if="device == 'Custom'">
          <div class="md:w-2/12">
            <label for="width">Width (px)</label>
            <input
              class="w-full"
              name="width"
              v-model="scan.width"
              type="text"
              placeholder="Width"
            />
          </div>
          <div class="md:w-2/12 md:pl-2">
            <label for="height">Height (px)</label>
            <input
              class="w-full"
              name="height"
              v-model="scan.height"
              type="text"
              placeholder="Height"
            />
          </div>
        </div>
        <button
          class="btn mx-auto block mt-4"
          @click="startScan"
          :disabled="scanButtonDisabled"
        >
          <span v-if="!scanning">Scan now</span>
          <svg
            v-else
            class="inline-block animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </button>
      </div>

      <div
        class="max-w-2xl mx-auto px-8 py-8 mt-6 bg-white rounded-lg shadow-md relative"
      >
        <h2 class="mb-4 text-lg text-center border-b-2 pb-3 border-gray-100">
          Scan image files
        </h2>
        <div
          class="p-12 border-dashed border-2 border-gray-200 rounded-lg"
          @dragover="dragover"
          @dragleave="dragleave"
          @drop="drop"
        >
          <input
            type="file"
            multiple
            name="fields[assetsFieldHandle][]"
            id="assetsFieldHandle"
            class="w-px h-px opacity-0 overflow-hidden absolute"
            @change="handleImage"
            ref="file"
            accept=".png, .jpg, .jpeg"
          />
          <label for="assetsFieldHandle" class="block cursor-pointer">
            <div class="text-center" v-if="!dropNow">
              Drag and drop .png files or
              <span class="text-secondary underline">click here</span> to upload
            </div>
            <div class="text-center" v-else>Drop now to scan</div>
          </label>
        </div>
      </div>
    </div>
    <walkthrough-modal />
  </div>
</template>

<script>
// @ is an alias to /src
import { Auth, API, Analytics } from "aws-amplify";
import PageHeader from "@/components/PageHeader";
import WalkthroughModal from "../components/WalkthroughModal.vue";

export default {
  name: "Home",
  components: {
    PageHeader,
    WalkthroughModal,
  },
  data() {
    return {
      scanning: false,
      scan: {
        url: "https://",
        width: 1440,
        height: 1080,
        type: "overlay",
        location: "us",
        explanation: true,
      },
      file: null,
      image: null,
      dropNow: false,
      device: "Desktop",
      devices: [
        { name: "Desktop", width: 1440, height: 1080 },
        { name: "Laptop", width: 1366, height: 768 },
        { name: "iPhone 14 Pro", width: 390, height: 844 },
        { name: "iPhone 14 Pro Max", width: 428, height: 926 },
        { name: "iPhone 14", width: 360, height: 780 },
        { name: "iPad Pro (Portrait)", width: 834, height: 1112 },
        { name: "iPad Pro (Landscape)", width: 1112, height: 834 },
        { name: "iPad (Portrait)", width: 768, height: 1024 },
        { name: "iPad (Landscape)", width: 1024, height: 768 },
        { name: "Custom", width: null, height: null },
      ],
    };
  },
  computed: {
    scanButtonDisabled() {
      function validURL(str) {
        var valid = /^(ftp|http|https):\/\/[^ "]+$/.test(str);
        return valid;
      }
      if (this.scan.width && !Number.isInteger(this.scan.width * 1))
        return true;
      if (this.scan.height && !Number.isInteger(this.scan.height * 1))
        return true;
      if (!validURL(this.scan.url)) return true;
      return false;
    },
  },
  methods: {
    startScan() {
      Analytics.record({ name: "startedScan" });
      this.scanning = true;
      this.$emit("scan", this.scan);
      this.scan = {
        url: "https://",
        width: null,
        height: null,
        type: "overlay",
        location: "us",
        explanation: true,
      };
      this.device = "Desktop";
      this.scanning = false;
    },
    async updateItem(scan) {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const token = user.signInUserSession.idToken.jwtToken;

      API.put("scans", `/scans`, {
        body: {
          ...scan,
          updated: true,
        },
        headers: {
          Authorization: token,
        },
      })
        .then((result) => {
          console.log(result);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    handleImage() {
      this.file = this.$refs.file.files[0];
      if (this.file.type == "image/png" || this.file.type == "image/jpeg") {
        if (this.file.size > 6 * 1024 * 1024) {
          alert(
            "Sorry, that file is too large. Please use an image that is 8Mb or less"
          );
        } else {
          this.createBase64Image(this.file);
          Analytics.record({ name: "uploadedImage" });
        }
      } else {
        alert("Please use a .png file");
      }
    },
    createBase64Image(fileObject) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.image = e.target.result;
        let data = {
          name: this.file.name,
          imageData: this.image,
        };
        this.$emit("scan", data);
      };
      reader.readAsDataURL(fileObject);
    },
    dragover(event) {
      event.preventDefault();
      // Add some visual fluff to show the user can drop its files
      this.dropNow = true;
      if (!event.currentTarget.classList.contains("border-green-500")) {
        event.currentTarget.classList.remove("border-gray-100");
        event.currentTarget.classList.add("border-green-500");
      }
    },
    dragleave(event) {
      // Clean up
      this.dropNow = false;
      event.currentTarget.classList.add("border-gray-100");
      event.currentTarget.classList.remove("border-green-500");
    },
    drop(event) {
      event.preventDefault();
      this.dropNow = false;
      this.$refs.file.files = event.dataTransfer.files;
      this.handleImage(); // Trigger the onChange event manually
      // Clean up
      event.currentTarget.classList.add("border-gray-100");
      event.currentTarget.classList.remove("border-green-500");
    },
  },
  watch: {
    device: function (val) {
      if (val != "Custom") {
        this.scan.width = this.devices.find((d) => d.name == val).width;
        this.scan.height = this.devices.find((d) => d.name == val).height;
      }
    },
  },
  created() {
    Analytics.record({ name: "viewedHome" });
  },
};
</script>
